import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RegistrationRequestService } from '@/services/user/registrationRequestService';
import { 
  RegistrationRequest, 
  RegistrationRequestSummary,
} from '@/types/registrationRequest';

export const useRegistrationRequests = (
  page: number = 1,
  limit: number = 15,
  status?: string,
  userType?: string,
  requestId?: string,
  search?: string,
  options?: { enabled?: boolean }
) => {
  const queryClient = useQueryClient();
  const isEnabled = options?.enabled !== false; // Default to true if not specified

  // Get all registration requests query (for overview/list views)
  const allRequestsQuery = useQuery<{
    requests: RegistrationRequestSummary[];
    totalCount: number;
    totalPages: number;
    currentPage: number;
  }>({
    queryKey: ['registrationRequests', page, limit, status, userType, search],
    queryFn: () => RegistrationRequestService.getAllRegistrationRequests(
      page, 
      limit, 
      status as "pending" | "approved" | "rejected" | undefined,
      userType as "dealer" | "business" | undefined,
      search
    ),
    enabled: isEnabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Get registration request by ID query (for detailed views)
  const requestQuery = useQuery<RegistrationRequest>({
    queryKey: ['registrationRequest', requestId],
    queryFn: () => RegistrationRequestService.getRegistrationRequestById(requestId as string),
    enabled: isEnabled && Boolean(requestId),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Approve registration request mutation
  const approveRequestMutation = useMutation({
    mutationFn: ({ requestId, reviewNotes }: { requestId: string; reviewNotes?: string }) =>
      RegistrationRequestService.approveRegistrationRequest(requestId, reviewNotes),
    onSuccess: () => {
      // Invalidate and refetch specific request and all requests queries
      if (requestId) {
        queryClient.invalidateQueries({ queryKey: ['registrationRequest', requestId] });
      }
      queryClient.invalidateQueries({ queryKey: ['registrationRequests'] });
      // Also invalidate stats so the pending count updates immediately
      queryClient.invalidateQueries({ queryKey: ['registrationApplicationStats'] });
      // Invalidate user management queries so the newly approved user appears in the user list
      queryClient.invalidateQueries({ queryKey: ['userManagement'] });
    },
  });

  // Reject registration request mutation
  const rejectRequestMutation = useMutation({
    mutationFn: ({ requestId, rejectionReason, reviewNotes }: { requestId: string; rejectionReason: string; reviewNotes?: string }) =>
      RegistrationRequestService.rejectRegistrationRequest(requestId, rejectionReason, reviewNotes),
    onSuccess: (_, variables) => {
      // Invalidate and refetch specific request and all requests queries
      if (variables.requestId) {
        queryClient.invalidateQueries({ queryKey: ['registrationRequest', variables.requestId] });
      }
      queryClient.invalidateQueries({ queryKey: ['registrationRequests'] });
      // Also invalidate stats so the pending count updates immediately
      queryClient.invalidateQueries({ queryKey: ['registrationApplicationStats'] });
      // Invalidate user management queries
      queryClient.invalidateQueries({ queryKey: ['userManagement'] });
    },
  });

  // Get registration application stats query
  const statsQuery = useQuery({
    queryKey: ['registrationApplicationStats'],
    queryFn: () => RegistrationRequestService.getRegistrationApplicationStats(),
    enabled: isEnabled,
    staleTime: 0, // Always refetch when needed (e.g., when navigating to dashboard)
    refetchOnMount: true, // Refetch when component mounts
  });

  // Bulk approve registration applications mutation
  const bulkApproveMutation = useMutation({
    mutationFn: ({ requestIds, reviewNotes }: { requestIds: string[]; reviewNotes?: string }) =>
      RegistrationRequestService.bulkApproveRegistrationApplications(requestIds, reviewNotes),
    onSuccess: () => {
      // Invalidate all requests queries
      queryClient.invalidateQueries({ queryKey: ['registrationRequests'] });
      queryClient.invalidateQueries({ queryKey: ['registrationApplicationStats'] });
      // Invalidate user management queries so the newly approved users appear in the user list
      queryClient.invalidateQueries({ queryKey: ['userManagement'] });
    },
  });

  return {
    // Query results for lists
    requests: allRequestsQuery.data?.requests || [],
    totalCount: allRequestsQuery.data?.totalCount || 0,
    totalPages: allRequestsQuery.data?.totalPages || 0,
    currentPage: allRequestsQuery.data?.currentPage || 1,
    isLoadingRequests: allRequestsQuery.isLoading,
    requestsError: allRequestsQuery.error,
    refetchRequests: allRequestsQuery.refetch,

    // Query results for single request
    request: requestQuery.data,
    isLoadingRequest: requestQuery.isLoading,
    requestError: requestQuery.error,
    refetchRequest: requestQuery.refetch,

    // Stats query
    stats: statsQuery.data,
    isLoadingStats: statsQuery.isLoading,
    statsError: statsQuery.error,
    refetchStats: statsQuery.refetch,

    // Mutations
    approveRequestAsync: approveRequestMutation.mutateAsync,
    rejectRequestAsync: rejectRequestMutation.mutateAsync,
    isApprovingRequest: approveRequestMutation.isPending,
    isRejectingRequest: rejectRequestMutation.isPending,
    approveRequestError: approveRequestMutation.error,
    rejectRequestError: rejectRequestMutation.error,

    // Bulk approve mutation
    bulkApproveAsync: bulkApproveMutation.mutateAsync,
    isBulkApproving: bulkApproveMutation.isPending,
    bulkApproveError: bulkApproveMutation.error,
  };
};